import React, { useCallback, useMemo, useRef, useState } from 'react';

import alertCaller from '@core/app/actions/alert-caller';
import progressCaller from '@core/app/actions/progress-caller';
import { bb2PerspectiveGrid, bb2PerspectiveGridWide, bb2PnPPoints } from '@core/app/constants/fisheyeCameraConstants';
import { setFisheyeConfig } from '@core/helpers/camera-calibration-helper';
import checkDeviceStatus from '@core/helpers/check-device-status';
import deviceMaster from '@core/helpers/device-master';
import useI18n from '@core/helpers/useI18n';
import type { FisheyeCameraParametersV3, FisheyeCameraParametersV3Cali } from '@core/interfaces/FisheyePreview';

import styles from './Calibration.module.scss';
import ChArUco from './common/ChArUco';
import CheckPnP from './common/CheckPnP';
import downloadCalibrationFile from './common/downloadCalibrationFile';
import Instruction from './common/Instruction';
import moveLaserHead from './common/moveLaserHead';
import SolvePnP from './common/SolvePnP';

/* eslint-disable perfectionist/sort-enums */
enum Steps {
  PRE_CHESSBOARD = 1, // For advanced usages
  CHESSBOARD = 2, // For advanced usages
  PUT_PAPER = 3,
  SOLVE_PNP_INSTRUCTION = 4,
  SOLVE_PNP = 5,
  CHECK_PNP = 6,
}
/* eslint-enable perfectionist/sort-enums */

interface Props {
  currentData?: FisheyeCameraParametersV3Cali;
  isAdvanced: boolean;
  isOblique?: boolean;
  onClose: (completed?: boolean) => void;
}

const PROGRESS_ID = 'laser-head-fisheye-calibration';
// TODO: test on different devices
const DEFAULT_POINTS: Array<[number, number]> = [
  [2000, 1716],
  [3283, 1720],
  [2009, 2564],
  [3272, 2563],
  [2325, 1929],
  [2965, 1933],
  [2327, 2353],
  [2962, 2351],
];

const DEFAULT_POINTS_OBLIQUE: Array<[number, number]> = [
  [1625, 1078],
  [3490, 945],
  [2038, 2192],
  [3385, 2096],
  [2179, 1400],
  [3030, 1339],
  [2320, 1951],
  [3041, 1900],
];

/**
 * LaserHeadFisheye
 * calibration the fisheye camera on the laser head (bb2, hexa rf)
 */
const LaserHeadFisheyeCalibration = ({ currentData, isAdvanced, isOblique, onClose }: Props): React.JSX.Element => {
  const lang = useI18n();
  const tCali = lang.calibration;
  const calibratingParam = useRef<FisheyeCameraParametersV3Cali>(currentData ?? {});
  const [step, setStep] = useState<Steps>(isAdvanced ? Steps.PRE_CHESSBOARD : Steps.PUT_PAPER);
  const updateParam = useCallback((param: FisheyeCameraParametersV3Cali) => {
    calibratingParam.current = { ...calibratingParam.current, ...param };
  }, []);
  const model = useMemo(() => deviceMaster.currentDevice?.info.model ?? 'fbb2', []);
  const isHexaRf = useMemo(() => model === 'fhx2rf', [model]);

  if (step === Steps.PRE_CHESSBOARD) {
    return (
      <Instruction
        animationSrcs={[
          { src: 'video/laser-head-calibration/1-charuco.webm', type: 'video/webm' },
          { src: 'video/laser-head-calibration/1-charuco.mp4', type: 'video/mp4' },
        ]}
        buttons={[
          {
            label: tCali.next,
            onClick: () => setStep(Steps.CHESSBOARD),
            type: 'primary',
          },
        ]}
        onClose={onClose}
        steps={[tCali.put_charuco_1, tCali.put_charuco_2, tCali.put_charuco_3]}
        title={tCali.put_charuco}
      >
        <div className={styles.link} onClick={() => downloadCalibrationFile('assets/charuco-15-10-with-mark.pdf')}>
          {tCali.download_calibration_pattern}
        </div>
      </Instruction>
    );
  }

  if (step === Steps.CHESSBOARD) {
    return (
      <ChArUco
        calibrationThresholds={{ average: 3.5, good: 2.8 }}
        cameraIndex={0}
        isFisheye
        isVertical
        onClose={onClose}
        onNext={() => setStep(Steps.PUT_PAPER)}
        onPrev={() => setStep(Steps.PRE_CHESSBOARD)}
        steps={[
          {
            descriptions: [tCali.charuco_move_to_point_a, tCali.charuco_capture],
            imageUrl: 'core-img/calibration/charuco-a.jpg',
            key: 'left',
            name: 'A',
          },
          {
            descriptions: [tCali.charuco_move_to_point_b, tCali.charuco_capture],
            imageUrl: 'core-img/calibration/charuco-b.jpg',
            key: 'right',
            name: 'B',
          },
        ]}
        updateParam={updateParam}
      />
    );
  }

  if (step === Steps.PUT_PAPER) {
    const handleNext = async (doEngraving = true) => {
      const deviceStatus = await checkDeviceStatus(deviceMaster.currentDevice!.info);

      if (!deviceStatus) {
        return;
      }

      try {
        progressCaller.openNonstopProgress({
          id: PROGRESS_ID,
          message: tCali.drawing_calibration_image,
        });

        if (doEngraving) {
          if (isHexaRf) await deviceMaster.doHexa2Calibration();
          else await deviceMaster.doBB2Calibration();
        }

        progressCaller.update(PROGRESS_ID, { message: tCali.preparing_to_take_picture });

        const res = await moveLaserHead();

        if (!res) return;

        setStep(Steps.SOLVE_PNP_INSTRUCTION);
      } catch (err) {
        console.error(err);
      } finally {
        progressCaller.popById(PROGRESS_ID);
      }
    };

    return (
      <Instruction
        animationSrcs={[
          { src: 'video/laser-head-calibration/2-cut.webm', type: 'video/webm' },
          { src: 'video/laser-head-calibration/2-cut.mp4', type: 'video/mp4' },
        ]}
        buttons={[
          isAdvanced
            ? { label: tCali.back, onClick: () => setStep(Steps.CHESSBOARD) }
            : {
                label: tCali.cancel,
                onClick: () => onClose(false),
              },
          { label: tCali.skip, onClick: () => handleNext(false) },
          { label: tCali.start_engrave, onClick: () => handleNext(), type: 'primary' },
        ]}
        onClose={() => onClose(false)}
        steps={[
          tCali.put_paper_step1,
          tCali.put_paper_step2,
          tCali.perform_autofocus_bb2,
          tCali.put_paper_step3,
          tCali.put_paper_skip,
        ]}
        title={tCali.put_paper}
      />
    );
  }

  if (step === Steps.SOLVE_PNP_INSTRUCTION) {
    return (
      <Instruction
        animationSrcs={[
          { src: 'video/laser-head-calibration/3-align.webm', type: 'video/webm' },
          { src: 'video/laser-head-calibration/3-align.mp4', type: 'video/mp4' },
        ]}
        buttons={[
          { label: tCali.back, onClick: () => setStep(Steps.PUT_PAPER) },
          { label: tCali.next, onClick: () => setStep(Steps.SOLVE_PNP), type: 'primary' },
        ]}
        onClose={() => onClose(false)}
        steps={[tCali.solve_pnp_step1, tCali.solve_pnp_step2]}
        title={tCali.solve_pnp_title}
      />
    );
  }

  if (step === Steps.SOLVE_PNP) {
    return (
      <SolvePnP
        cameraIndex={0}
        defaultPoints={isOblique ? DEFAULT_POINTS_OBLIQUE : DEFAULT_POINTS}
        dh={0}
        hasNext
        initPoseWithDefaultPoints={isAdvanced}
        onBack={() => setStep(Steps.SOLVE_PNP_INSTRUCTION)}
        onClose={onClose}
        onNext={async (rvec, tvec) => {
          progressCaller.openNonstopProgress({ id: PROGRESS_ID, message: lang.device.processing });
          updateParam({ rvec, tvec });
          console.log('calibratingParam.current', calibratingParam.current);
          progressCaller.popById(PROGRESS_ID);
          setStep(Steps.CHECK_PNP);
        }}
        params={calibratingParam.current}
        refPoints={bb2PnPPoints}
      />
    );
  }

  if (step === Steps.CHECK_PNP) {
    return (
      <CheckPnP
        cameraOptions={{ index: 0 }}
        dh={0}
        grid={isOblique ? bb2PerspectiveGridWide : bb2PerspectiveGrid}
        onBack={() => setStep(Steps.SOLVE_PNP)}
        onClose={onClose}
        onNext={async () => {
          const param: FisheyeCameraParametersV3 = {
            d: calibratingParam.current.d!,
            k: calibratingParam.current.k!,
            rvec: calibratingParam.current.rvec!,
            tvec: calibratingParam.current.tvec!,
            v: 3,
          };
          const res = await setFisheyeConfig(param);

          if (res.status === 'ok') {
            alertCaller.popUp({ message: tCali.camera_parameter_saved_successfully });
            onClose(true);
          } else {
            alertCaller.popUpError({
              message: `${tCali.failed_to_save_camera_parameter}:<br />${JSON.stringify(res)}`,
            });
          }
        }}
        params={{
          d: calibratingParam.current.d!,
          k: calibratingParam.current.k!,
          rvec: calibratingParam.current.rvec!,
          tvec: calibratingParam.current.tvec!,
        }}
        points={bb2PnPPoints}
      />
    );
  }

  onClose();

  return <></>;
};

export default LaserHeadFisheyeCalibration;
